import type { Lint, Linter } from "harper.js";

export const SUPPORTED_SPELLCHECK_LANGUAGES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "en-CA", label: "English (Canada)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-IN", label: "English (India)" },
] as const;

export type SupportedSpellcheckLanguage = (typeof SUPPORTED_SPELLCHECK_LANGUAGES)[number]["value"];

export const DEFAULT_SPELLCHECK_ENABLED = true;
export const DEFAULT_SPELLCHECK_LANGUAGE: SupportedSpellcheckLanguage = "en-US";

export function isSupportedSpellcheckLanguage(
  value: unknown
): value is SupportedSpellcheckLanguage {
  return (
    typeof value === "string" &&
    SUPPORTED_SPELLCHECK_LANGUAGES.some((language) => language.value === value)
  );
}

export interface SpellcheckMatch {
  id: string;
  offset: number;
  length: number;
  kind: string;
  problemText: string;
  message: string;
  suggestions: string[];
  sourceText: string;
  lint: Lint;
}

export interface SpellcheckProofreader {
  lint(text: string): Promise<SpellcheckMatch[]>;
  ignore(match: SpellcheckMatch): Promise<void>;
}

export type SpellcheckLinter = Pick<Linter, "clearWords" | "ignoreLint" | "importWords" | "lint">;

export type SpellcheckStatus = "disabled" | "loading" | "ready" | "checking" | "unavailable";

/**
 * Coarse engine state published on the editor as `data-spellcheck-state`.
 *
 * `SpellcheckStatus` cannot be used for this: it reports the *lint pass*, and a
 * pass over a suspended proofreader returns no matches and reports "ready" before
 * Harper's 17.4 MiB WASM bundle has even finished compiling. "active" here means
 * the linter is loaded AND its dictionary is synced — i.e. an unmarked word really
 * is a word the engine accepted, not one it never looked at.
 */
export type SpellcheckReadiness = "disabled" | "loading" | "active" | "unavailable";

export interface SpellcheckOpenMatchPayload {
  id: string;
  problemText: string;
  message: string;
  suggestions: string[];
  from: number;
  to: number;
  rect: DOMRect;
}

export interface SpellcheckDictionaryActions {
  canAddOrgWord: boolean;
  canAddPlatformWord: boolean;
  isAddingWord: boolean;
  addOrgWord: (word: string) => Promise<void>;
  addPlatformWord: (word: string) => Promise<void>;
}
